import type { ApiResponse, GetListResponse } from './generic';
import type { Employee } from './employee';
import type { OptionalPageLimit } from './common';
import type { LeaveRequestItem } from './leaves';
import type { ApprovalRoute, LeaveApprovalProgress } from './approvals';

export enum LeaveStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Completed = 'Completed',
  Active = 'Active',
  Inactive = 'Inactive',
}

export interface LeaveRequest {
  id: string;
  employee: Employee;
  leaveType: {
    code: string;
    name: string;
    color: string;
  };
  duration: number;
  dateRange: {
    start: string;
    end: string;
  };
  reason: string;
  status: LeaveStatus;
}

export enum PolicyStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export interface Policy {
  id: string;
  name: string;
  description: string;
  status: PolicyStatus;
  employeeCount: number;
  leaveTypes: Array<{
    code: string;
    name: string;
    allocation: string;
    yearEndAction?: string;
  }>;
  effectiveDate: string;
  targetAudience: string;
}

export interface Holiday {
  id: string;
  name: string;
  leaveDate: string;
  description: string;
}

export enum EntitlementAllocationType {
  MonthlyAccrual = 'MONTHLY_ACCRUAL',
  Quarterly = 'QUARTERLY',
  halfYearly = 'HALF_YEARLY',
  OneTime = 'ONE_TIME',
}

export type AllocationTypeResponse =
  'monthly' | 'quarterly' | 'half_yearly' | 'yearly' | 'one_time';

export enum YearEndAction {
  CarryForward = 'CARRY_FORWARD',
  Expire = 'EXPIRE',
}

export enum CarryLimitType {
  AllUnusedLeaves = 'ALL_UNUSED_LEAVES',
  Limited = 'LIMITED',
  FixedMaxDays = 'FIXED_MAX_DAYS',
}

/**
 * How a policy leave type counts (spec 2026-10-21): `balance` has an
 * allowance and a wallet; `record_only` is an absence that is only recorded,
 * with no balance ever. Fixed once the rule exists.
 */
export enum LeaveTracking {
  Balance = 'balance',
  RecordOnly = 'record_only',
}

/**
 * Supporting document ("justificatif") on a request of a rule
 * (2026-10-21): `optional` shows the attach button, `required` blocks an
 * employee submit without a file, `none` takes no file.
 */
export enum SupportingDocumentRule {
  None = 'none',
  Optional = 'optional',
  Required = 'required',
}

/** At most 3 files per request, PDF / JPEG / PNG / HEIC, 10 MB each. */
export const LEAVE_ATTACHMENT_MAX_FILES = 3;
export const LEAVE_ATTACHMENT_MAX_MB = 10;
export const LEAVE_ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic';
export type LeaveAttachmentContentType =
  'application/pdf' | 'image/jpeg' | 'image/png' | 'image/heic';

/** A stored supporting document, as every reader gets it (signed URL). */
export interface LeaveRequestAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  url: string | null;
}

/** A file already PUT to S3, sent with a create (or an edit, to add). */
export interface LeaveAttachmentInput {
  key: string;
  fileName: string;
  contentType: LeaveAttachmentContentType;
  sizeBytes: number;
}

/** What an upload URL route answers. */
export interface LeaveAttachmentUploadTicket {
  key: string;
  url: string;
  maxBytes: number;
}

export interface LeaveEntitlement {
  id: string;
  leaveType: string;
  allocationType: EntitlementAllocationType;
  allocationMonth: string;
  allowance: number;
  isAutoApproved: boolean;
  /** Default `balance`; omitted on rules saved before 2026-10-21. */
  tracking?: LeaveTracking;
  /** Paid or unpaid absence (payroll and timesheet exports). Default true. */
  isPaid?: boolean;
  /** The employee must give a reason. Default false. */
  reasonRequired?: boolean;
  /** false: an HR-only type (employees cannot request it). Default true. */
  employeeCanRequest?: boolean;
  /** Default `optional`. */
  supportingDocument?: SupportingDocumentRule;
  /** This rule's own approval route; null = the company route (D187). */
  approvalRoute?: ApprovalRoute | null;
  minAdvanceDays: number;
  proRata: boolean;
  yearEndAction: YearEndAction;
  carryForwardLimit?: number;
  carryLimitType?: CarryLimitType;
  /** Length-of-service bounds in months (e.g. 6, 12, 18). */
  minMonthsOfService?: number;
  maxMonthsOfService?: number;
}

export interface CompanySchedule {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  description: string;
  activePolicies: string[];
}

export interface GetLeaveRequestsSummaryParams {
  companyId: string;
  branchId?: string;
}

export interface LeaveRequestsSummary {
  pendingRequests: number;
  approvedThisMonth: number;
  rejectedThisMonth: number;
}

export type GetLeaveRequestsSummaryResponse = ApiResponse<LeaveRequestsSummary>;

export type CompanyLeaveRequestsResponse =
  GetListResponse<CompanyLeaveRequestItem>;

export enum CompanyLeaveStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  AutoApproved = 'auto_approved',
  Cancelled = 'cancelled',
}
interface ApprovedByUser {
  firstName: string;
  lastName: string;
}
interface RejectedByUser {
  firstName: string;
  lastName: string;
}

interface CancelledByUser {
  firstName: string;
  lastName: string;
}

/** A pending request's balance at decision time (days). */
export interface LeaveBalanceCheck {
  /** Left in the wallet now. */
  available: number;
  /** What the request would deduct. */
  requested: number;
  /** Left after approving it; negative when the wallet cannot cover it. */
  after: number;
  insufficient: boolean;
}

/** `person` = a named approver, `hr` = the HR queue, `none` = no approver. */
export type LeaveWaitingOnKind = 'person' | 'hr' | 'none';

export interface LeaveWaitingOn {
  kind: LeaveWaitingOnKind;
  /** `employee_leave_requests.owner_type` (manager, assigned_approver, ...). */
  ownerType: string | null;
  name: string | null;
}

/** Who a pending request waits on and for how long. */
export interface LeavePendingInsight {
  waitingOn: LeaveWaitingOn;
  /** Whole days since it was submitted. */
  pendingDays: number;
  /** Older than the company's overdue threshold (escalation setting, else 72h). */
  overdue: boolean;
}

/** The current reporting-line manager as a leave row carries it. */
export interface LeaveRowReportingManager {
  employeeId: string;
  userId: string;
  firstName: string;
  lastName: string;
  profileImage: string | null;
  /** A temporary line (a handover's interim manager). */
  isInterim: boolean;
}

/** A teammate off during the request's dates. */
export interface LeaveTeamAvailabilityEntry {
  employeeId: string;
  name: string;
  relation: 'manager' | 'peer';
  startDate: string;
  endDate: string;
  status: 'approved' | 'pending';
}

/**
 * Who on the requester's team is off during a request, shown inline wherever
 * it can be approved or declined (JP, 2026-10-01). The mobile app reads this
 * exact shape.
 */
export interface TeamOverlap {
  /** Distinct teammates (and the manager) off during the request. */
  count: number;
  /** The first three, earliest leave first. */
  people: {
    name: string;
    startDate: string;
    endDate: string;
    status: 'approved' | 'pending';
  }[];
}

/** What a details modal adds to a request to make it decision-ready. */
export interface LeaveDecisionContext {
  requestId: string;
  status: string;
  reportingManager: {
    employeeId: string;
    name: string;
    isInterim: boolean;
  } | null;
  pending: LeavePendingInsight | null;
  overdueThresholdHours: number;
  balanceCheck: LeaveBalanceCheck | null;
  teamAvailability: LeaveTeamAvailabilityEntry[];
  /** Automatic reminders sent to the owner so far (max 3). */
  remindersSent?: number;
  /** When the last reminder went (ISO), or null. */
  lastReminderAt?: string | null;
  /** Supporting documents on the request (signed URLs). */
  attachments?: LeaveRequestAttachment[];
  /** Where it is in its approval route (step chip, decision history). */
  approval?: LeaveApprovalProgress | null;
}

export type LeaveDecisionContextResponse = ApiResponse<LeaveDecisionContext>;

export interface CompanyLeaveRequestItem extends LeaveRequestItem {
  id: string;
  companyId: string;
  employeeId: string;
  leaveWalletId: string;
  status: CompanyLeaveStatus;
  reason: string | null;
  rejectionReason: string | null;
  cancellationReason: string | null;
  createdAt: string;
  approvedAt: string;
  rejectedAt: string | null;
  cancelledAt: string | null;
  approvedByUserId: string | null;
  rejectedByUserId: string | null;
  cancelledByUserId: string | null;
  createdByUserId: string;
  approvedByUser: ApprovedByUser;
  rejectedByUser: RejectedByUser;
  cancelledByUser: CancelledByUser | null;
  /** Hours in this employee's working day (a part-timer's own day). */
  hoursPerDay?: number;

  employee: Employee & {
    /** Current reporting-line manager (interim lines included), or null. */
    reportingManager?: LeaveRowReportingManager | null;
  };
  /** Set while pending: who it waits on, for how long, overdue or not. */
  pending?: LeavePendingInsight | null;
  /** Set while pending: who on the team is off on those dates. */
  teamOverlap?: TeamOverlap | null;
  /** Set while pending: the balance check at decision time. */
  balanceCheck?: LeaveBalanceCheck | null;
  /**
   * Whether the viewer may approve or decline it now, by the server's own
   * eligibility rule (assigned approver, HR queue or override, manager,
   * delegate). Absent from older API responses.
   */
  canDecide?: boolean;
  leaveWallet: {
    id: string;
    leaveType: {
      id: string;
      name: string;
      description: string | null;
    };
  };
}

export interface GetCompanyLeaveRequestsParams extends OptionalPageLimit {
  companyId?: string;
  branchId?: string;
  status?: CompanyLeaveStatus[];
  search?: string;
  employeeIds: string[];
  departmentIds?: string[];
  managerIds?: string[];
  dateRange?: {
    fromDate?: string;
    toDate?: string;
  };
  sortBy?: LeaveRequestSortBy;
  sortOrder?: SortOrder;
  /** Leave out pending requests whose leave already ended (for "act now"
   *  lists such as the dashboard; Leave Management keeps them). */
  excludeLapsed?: boolean;
  /** Leave out every request whose leave ended before today (any status). */
  hidePast?: boolean;
}

export enum LeaveRequestSortBy {
  EMPLOYEE_NAME = 'employeeName',
  APPLIED_ON = 'appliedOn',
  APPROVED_ON = 'approvedOn',
  STATUS = 'status',
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

// Calendar API Types
export interface CalendarLeave {
  leaveRequestId: string;
  /**
   * First date of the whole leave request (`yyyy-MM-dd`). The per-date records
   * are clamped to the queried window; this pair never is.
   */
  startDate: string;
  /** Last date of the whole leave request (`yyyy-MM-dd`). */
  endDate: string;
  status: string;
  reason: string;
  startSession?: string;
  endSession?: string;
  startTime?: string;
  endTime?: string;
  leaveType: {
    id: string;
    name: string;
  };
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string | null;
  };
  /** Formatted duration string from the backend (e.g. "24 hours", "3 days"). */
  duration?: string;
  /** Human-readable note about excluded non-working days (e.g. "Doesn't count 1 holiday and 2 weekend days"). */
  excludedDaysNote?: string;
}

export interface CalendarHoliday {
  date: string;
  name: string;
  description: string;
}

export type CompanyLeaveCalendarResponse = ApiResponse<{
  startDate: string;
  endDate: string;
  holidays: CalendarHoliday[];
  leaves: Record<string, CalendarLeave[]>;
}>;

export interface GetCompanyLeaveCalendarParams {
  companyId: string;
  startDate: string;
  endDate: string;
  leaveTypeIds?: string;
  employeeIds?: string;
  branchId?: string;
}

/** Same payload shape as CompanyLeaveCalendarResponse — the employee
 *  dashboard's own on-leave endpoint has no companyId/branch params since
 *  those are resolved from the caller's own JWT, not the request. Also
 *  carries `totalEmployeeCount` so the card can show "X in · Y out", which
 *  the admin calendar endpoint doesn't need since it has its own dashboard
 *  summary for that. */
export type EmployeeLeaveCalendarResponse = ApiResponse<{
  startDate: string;
  endDate: string;
  totalEmployeeCount: number;
  holidays: CalendarHoliday[];
  leaves: Record<string, CalendarLeave[]>;
}>;

export interface GetEmployeeLeaveCalendarParams {
  startDate: string;
  endDate: string;
}

export interface EmployeeWeekendsData {
  weekends: number[];
}

export type EmployeeWeekendsResponse = ApiResponse<EmployeeWeekendsData>;

export interface LeaveJob {
  id: string;
  scheduledDate: string;
  processStartedAt: string | null;
  processCompletedAt: string | null;
  jobStatus: LeaveJobStatus;
  leavePolicy: LeavePolicy;
  policyLeaveType: PolicyLeaveType;
}

export type LeaveJobStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

export interface LeavePolicy {
  id: string;
  name: string;
}

export interface PolicyLeaveType {
  id: string;
  companyLeaveType: CompanyLeaveType;
}

export interface CompanyLeaveType {
  id: string;
  name: string;
}

export type LeaveJobResponse = GetListResponse<LeaveJob>;

export type AllocationJobStatus =
  'scheduled' | 'running' | 'completed' | 'failed';

export interface GetAllocationJobsParams extends OptionalPageLimit {
  companyId: string;
  policyId: string;
  status?: AllocationJobStatus;
  year?: number;
  sortOrder?: 'ASC' | 'DESC';
  leaveTypeIds: string[];
}

export interface CurrentFiscalStartYearData {
  currentYear: number;
}

export type GetCurrentFiscalStartYearResponse =
  ApiResponse<CurrentFiscalStartYearData>;

export interface ScheduledJobEmployee {
  id: string;
  operationType: 'credit' | 'debit' | string;
  leaves: number;
  createdAt: string;
  employee: {
    id: string;
    user: {
      firstName: string;
      lastName: string;
    };
  };
}

export type ScheduledJobEmployeeResponse =
  GetListResponse<ScheduledJobEmployee>;

export interface GetScheduledJobEmployeeDataParams {
  companyId: string;
  scheduledJobId: string;
  page?: number;
  limit?: number;
}

export interface YearOption {
  label: string;
  value: number;
}
